import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { gradeApi } from '../api.js';
import { useAuth } from '../auth/AuthContext.js';
import ConfirmationModal from './ConfirmationModal.js';
import EmailGradesModal from './EmailGradesModal.js';
import { useDispatch, useSelector } from 'react-redux';
import { editQuestions } from '../store/flexibleAssignmentSlice.js';
import { toast } from 'react-toastify';
import {
  LuBarChart3,
  LuFileEdit,
  LuDownload,
  LuSend,
  LuSparkles,
  LuClock,
  LuMousePointer,
  LuMousePointerClick
} from 'react-icons/lu';
import { FiMousePointer } from 'react-icons/fi';

import '../App.css';

function TopBar({
  assignment_in,
  submissions_in,
  topErrorMessages,
  setTopErrorMessages,
  section,
  setSection,
  publishAssignment,
  isEditMode,
  toggleEditMode,
  isReleaseMode,
  handleReleaseGradesClick
}) {
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [gradesPublished, setGradesPublished] = useState(false);
  const { user } = useAuth();
  const [dueDateError, setDueDateError] = useState(false);
  const [dueTimeError, setDueTimeError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateFocused, setDateFocused] = useState(false);
  const [timeFocused, setTimeFocused] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [isExportGradesModalOpen, setIsExportGradesModalOpen] = useState(false);
  const [exportScoreType, setExportScoreType] = useState('percentage');
  const [allowPregrade, setAllowPregrade] = useState(false);
  const [isEmailGradesModalOpen, setIsEmailGradesModalOpen] = useState(false);
  const [isEmailingGrades, setIsEmailingGrades] = useState(false);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const { data: draftData } = useSelector(state => state?.flexibleAssignmentReducer?.draftData);
  const isAssignmentPublished = assignment?.is_assignment_published;
  const dispatch = useDispatch();
  const isEssay = assignment_in?.type === 'essay';
  const isFlexibleSet = assignment?.type === 'flexible_problem_set';

  useEffect(() => {
    if (assignment_in) {
      console.log(assignment_in);
      setAssignment(assignment_in);
      setGradesPublished(assignment_in.is_grades_published);
      setDueDate(assignment_in.due_date);
      setDueTime(assignment_in.due_time);
      setAllowPregrade(assignment_in.allow_pregrade || false);

      // Only set section to review if URL doesn't already have a section specified
      if (assignment_in.is_assignment_published && !isEssay && !section) {
        setSection('review');
      }
    }
    if (submissions_in) {
      setSubmissions(submissions_in);
    }
  }, [assignment_in, submissions_in]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmPublish = () => {
    publishAssignment();
    setIsModalOpen(false);
  };

  const sendGradeEmails = async () => {
    try {
      setIsEmailingGrades(true);
      await gradeApi.post({
        path: `/api/assignment/${assignment.id}/email-grades/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setIsEmailingGrades(false);
      setIsEmailGradesModalOpen(false);
      window.location.reload(); // Reload page to show updated status
    } catch (error) {
      console.error('Error sending grade emails:', error);
      setTopErrorMessages([error.response?.data?.error || 'Failed to send grade emails']);
      setIsEmailingGrades(false);
      setIsEmailGradesModalOpen(false);
    }
  };

  const handleSkipEmailGrades = () => {
    setIsEmailGradesModalOpen(false);
    window.location.reload();
  };

  const downloadCSV = () => {
    setIsExportGradesModalOpen(true);
  };

  const confirmExportGrades = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment.id}/export/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          },
          params: {
            score_type: exportScoreType
          },
          responseType: 'blob'
        }
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = `assignment_grades_${assignment.id}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setIsExportGradesModalOpen(false);
    } catch (error) {
      setTopErrorMessages(['Failed to export grades. Please try again.']);
    }
  };

  const updateDueDate = async () => {
    if (dueDateError) {
      setDueDateError(false);
      setTopErrorMessages(['']);
    }

    try {
      await gradeApi.patch({
        path: `/api/assignment/${assignment.id}/due-date`,
        body: {
          due_date: dueDate
        },
        headers: {
          Authorization: `Bearer ${user.session_token}`
        }
      });
      setTopErrorMessages(['']);
      // console.log('Due date updated successfully');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || 'Failed to update due date. Please try again.';
      setTopErrorMessages([errorMessage]);
    }
  };

  const updateDueTime = async () => {
    try {
      await gradeApi.patch({
        path: `/api/assignment/${assignment.id}/due-time`,
        body: {
          due_time: dueTime
        },
        headers: {
          Authorization: `Bearer ${user.session_token}`
        }
      });
      setTopErrorMessages(['']);
      // console.log('Due time updated successfully');
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update due time. Please try again.';

      setTopErrorMessages([errorMessage]);
    }
  };

  const checkAnalysisStatus = useCallback(
    async assignmentId => {
      if (!assignmentId || !user?.session_token) {
        return false;
      }

      try {
        const response = await gradeApi.get({
          path: `/api/assignment/${assignmentId}/check-agg-feedback-status/`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });

        return response.data?.is_agg_feedback_done;
      } catch (error) {
        console.error('Error checking AI analysis status:', error);
        return false;
      }
    },
    [user]
  );

  const startPollingAnalysisStatus = useCallback(
    async assignmentId => {
      if (!assignmentId || isPolling) {
        return;
      }

      setIsPolling(true);
      let attempts = 0;
      const maxAttempts = 60; // 5 minutes (5s interval × 60)
      const interval = 5000; // Check every 5 seconds

      const pollStatus = async () => {
        attempts++;

        const isDone = await checkAnalysisStatus(assignmentId);

        if (isDone) {
          setIsPolling(false);
          setAnalysisStarted(false);

          // Dispatch a custom event that TAssignmentStats can listen for
          const analysisCompletedEvent = new CustomEvent('analysisCompleted', {
            detail: { assignmentId }
          });
          window.dispatchEvent(analysisCompletedEvent);

          toast.success('AI Analysis generation completed!');
          return;
        }

        if (attempts >= maxAttempts) {
          setIsPolling(false);
          console.log('Polling timed out after max attempts');
          return;
        }

        setTimeout(pollStatus, interval);
      };

      pollStatus();
    },
    [checkAnalysisStatus, isPolling]
  );

  const generateAssignmentInsights = async assignmentId => {
    setIsGeneratingAnalysis(true);

    try {
      const response = await gradeApi.post({
        path: `/api/assignment/${assignmentId}/store-agg-feedback/`,
        body: { is_grades_published: true },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });

      if (response.success || response.data) {
        toast.success('AI Analysis generation started successfully!');
        setAnalysisStarted(true);
        // Start polling for status after successful API call
        startPollingAnalysisStatus(assignmentId);
      } else {
        throw new Error('Failed to start AI analysis');
      }
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      toast.error('Failed to generate AI analysis. Please try again.');
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  const message = assignment?.is_exam
    ? 'Publishing this assignment will prevent you from making changes to it. The ' +
      'questions will NOT be revealed to students. Once published, you will get access to a PDF you ' +
      'can print for your students.'
    : isEssay
      ? 'Publishing this essay assignment will prevent you from making changes to it. The assignment will become visible to students and allow their submissions.'
      : 'Publishing this assignment will prevent you from making changes to it. ' +
        'The assignment will become visible to students and allow their submissions.';

  const renderSectionButtons = () => {
    if (isReleaseMode) {
      return null;
    }

    const buttons = [];
    buttons.push(
      <button
        key="review"
        className={`py-4 px-8 font-medium text-base focus:outline-none flex items-center gap-2 ${
          section === 'review'
            ? 'border-b-2 border-[#e4b17a] text-[#e4b17a] font-semibold'
            : 'text-gray-600 hover:text-gray-800'
        }`}
        onClick={() => {
          setSection('review');
        }}
      >
        <LuFileEdit className="w-4 h-4" /> Review
      </button>
    );

    if (!isFlexibleSet || isEssay) {
      // Define CSS animation for the finger click animation and pulsing glow
      const hasSubmissions =
        submissions &&
        submissions.entities &&
        submissions.entities.some(
          student => student.grade !== null || student.status !== 'Not submitted'
        );
      console.log(submissions);
      console.log('hasSubmissions', hasSubmissions);

      const statsButtonStyles = {
        position: 'relative',
        ...(hasSubmissions &&
          section !== 'stats' && {
            filter: 'drop-shadow(0 0 3px rgba(228, 177, 122, 0.6))',
            animation: 'subtle-pulse 2.5s infinite'
          })
      };

      buttons.push(
        <button
          key="stats"
          className={`py-4 px-8 font-medium text-base focus:outline-none flex items-center gap-2 ${
            section === 'stats'
              ? 'border-b-2 border-[#e4b17a] text-[#e4b17a] font-semibold'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          style={statsButtonStyles}
          onClick={() => {
            setSection('stats');
          }}
        >
          <LuBarChart3 className="w-4 h-4" />
          <div className="relative">
            Stats
            {hasSubmissions && section !== 'stats' && (
              <div
                className="stats-indicator"
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '70%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}
              >
                <div className="pointer-container">
                  <FiMousePointer className="mouse-pointer" size={16} color="#e4b17a" />
                </div>

                {/* Ripple effect directly over Stats text */}
                <div
                  className="click-ripple-container"
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '30%',
                    transform: 'translateX(-50%)',
                    width: '15px',
                    height: '15px',
                    opacity: 0.7
                  }}
                >
                  <div className="click-ripple"></div>
                </div>
              </div>
            )}
          </div>
        </button>
      );
    }

    return <div className="flex flex-row">{buttons}</div>;
  };

  return (
    <div
      id="HomeTeacher"
      className={`flex flex-col gap-0 fixed z-40 bg-white pt-6 topbar-padding${
        section === 'review' || section === 'stats' || isFlexibleSet || isEssay
          ? 'ml left-side-bar-size w-[calc(100%-20vw)] left-[270px] pr-8'
          : 'w-full px-10'
      }`}
    >
      <style jsx global>{`
        @keyframes subtle-pulse {
          0% {
            filter: drop-shadow(0 0 3px rgba(228, 177, 122, 0.4));
          }
          50% {
            filter: drop-shadow(0 0 5px rgba(228, 177, 122, 0.7));
          }
          100% {
            filter: drop-shadow(0 0 3px rgba(228, 177, 122, 0.4));
          }
        }

        .pointer-container {
          position: relative;
          animation: float-vertical 1.5s ease-in-out infinite;
        }

        @keyframes float-vertical {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }

        .mouse-pointer {
          filter: drop-shadow(0 0 1px rgba(228, 177, 122, 0.3));
          animation: pulse-opacity 2s ease-in-out infinite;
        }

        @keyframes pulse-opacity {
          0%,
          100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }

        .click-ripple-container {
          position: relative;
        }

        .click-ripple {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(228, 177, 122, 0.6);
          border-radius: 50%;
          transform: scale(0);
          animation: click-ripple 1.5s infinite;
        }

        @keyframes click-ripple {
          0%,
          100% {
            transform: scale(0);
            opacity: 0;
          }
          10% {
            transform: scale(0);
            opacity: 0.8;
          }
          50% {
            transform: scale(10);
            opacity: 0;
          }
        }

        /* Glow animation for the Generate AI Analysis button */
        .generate-ai-glow {
          animation: ai-button-glow 2s infinite alternate;
          position: relative;
        }

        @keyframes ai-button-glow {
          0% {
            box-shadow: 0 0 5px rgba(228, 177, 122, 0.4);
          }
          100% {
            box-shadow:
              0 0 12px rgba(228, 177, 122, 0.7),
              0 0 20px rgba(228, 177, 122, 0.4);
          }
        }
      `}</style>

      <div id="Main-Content-container" className="flex flex-col space-y-4 w-full">
        {/* Section switcher */}
        {!isEssay && isAssignmentPublished && (
          <div className="flex sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center w-full border-b border-gray-200 pb-2">
              {/* Left side - Back button with assignment name */}
              <div className="flex items-center ml-5">
                <Link to={`/tclasses/${assignment_in.course_id}/tassignments`}>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center mr-3 hover:bg-gray-100 transition-colors">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 12L6 8L10 4"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 8H14"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-black topbar-text-size font-medium">
                      {assignment_in
                        ? assignment_in.title.length > 40
                          ? assignment_in.title.substring(0, 39) + '...'
                          : assignment_in.title
                        : ''}
                      {assignment_in?.is_group_task ? ' (Group)' : ''}
                    </span>
                  </div>
                </Link>
              </div>

              {/* Centered section tabs */}
              <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
                {renderSectionButtons()}
              </div>

              {/* Action buttons - aligned right */}
              <div className="flex-1 flex justify-end">
                {/* Review mode buttons */}
                {!isReleaseMode &&
                  assignment_in?.is_assignment_published &&
                  section === 'review' && (
                    <div className="flex gap-3">
                      <button
                        className="px-4 py-2 rounded-xl text-smallish font-medium transition-all duration-300 hover:-translate-y-[0.5px] shadow-sm flex items-center gap-1.5"
                        style={{
                          background: 'white',
                          border: '1px solid rgba(228, 177, 121, 0.2)',
                          color: '#8c6b4a',
                          boxShadow:
                            'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px'
                        }}
                        onClick={downloadCSV}
                      >
                        <LuDownload className="w-4 h-4" />
                        Download Grades
                      </button>

                      <button
                        className="px-4 py-2 rounded-xl text-smallish font-medium transition-all duration-300 hover:-translate-y-[0.5px] shadow-sm flex items-center gap-1.5"
                        style={{
                          background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                          border: 'none',
                          color: 'white',
                          boxShadow:
                            'rgba(228, 177, 122, 0.08) 0px 12px 24px -6px, rgba(228, 177, 122, 0.06) 0px 18px 36px -12px'
                        }}
                        onClick={handleReleaseGradesClick}
                      >
                        <LuSend className="w-4 h-4" />
                        Release Grades
                      </button>
                    </div>
                  )}

                {/* Stats mode buttons */}
                {!isReleaseMode &&
                  assignment_in?.is_assignment_published &&
                  section === 'stats' && (
                    <div className="flex gap-3 relative">
                      <button
                        className={`px-4 py-2 rounded-xl text-smallish font-medium transition-all duration-300 hover:-translate-y-[0.5px] shadow-sm flex items-center gap-1.5 relative ${!isGeneratingAnalysis && !analysisStarted && !assignment?.ai_analysis ? 'generate-ai-glow' : ''}`}
                        style={{
                          background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                          border: 'none',
                          color: 'white',
                          boxShadow:
                            'rgba(228, 177, 122, 0.08) 0px 12px 24px -6px, rgba(228, 177, 122, 0.06) 0px 18px 36px -12px',
                          transform: isGeneratingAnalysis ? 'scale(0.98)' : 'scale(1)'
                        }}
                        disabled={isGeneratingAnalysis || analysisStarted}
                        onClick={() => generateAssignmentInsights(assignment_in.id)}
                      >
                        <LuSparkles className="w-4 h-4" />
                        {isGeneratingAnalysis ? 'Generating...' : 'Generate AI Analysis'}
                      </button>

                      {/* Cursor pointer below the button */}
                      {!isGeneratingAnalysis && !analysisStarted && !assignment?.ai_analysis && (
                        <div
                          className="ai-analysis-indicator"
                          style={{
                            position: 'absolute',
                            top: '40px', // Moved below the button
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            pointerEvents: 'none' // Allow clicks to pass through
                          }}
                        >
                          <div className="pointer-container">
                            <FiMousePointer className="mouse-pointer" size={18} color="#e4b17a" />
                          </div>

                          {/* Ripple effect for AI Analysis */}
                          <div
                            className="click-ripple-container"
                            style={{
                              position: 'absolute',
                              top: '-24px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '15px',
                              height: '15px',
                              opacity: 0.8
                            }}
                          >
                            <div className="click-ripple"></div>
                          </div>
                        </div>
                      )}

                      {/* Analysis notification message as overlay */}
                      {analysisStarted && (
                        <div
                          className="absolute top-14 right-0 z-10 py-3 px-4 rounded-lg flex items-center gap-3 shadow-md"
                          style={{
                            width: '280px',
                            background: 'white',
                            border: '1px solid rgba(228, 177, 121, 0.15)',
                            color: '#8c6b4a'
                          }}
                        >
                          <LuClock className="w-5 h-5 flex-shrink-0" />
                          <div className="text-sm">
                            <span className="font-medium">Analysis in progress.</span> This may take
                            up to 5 minutes. Please wait.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={isExportGradesModalOpen}
          message="Are you sure you want to export the grades for this assignment? Download will start automatically."
          onClose={() => setIsExportGradesModalOpen(false)}
          onConfirm={confirmExportGrades}
        >
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Select score type:</label>
            <select
              value={exportScoreType}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              onChange={e => setExportScoreType(e.target.value)}
            >
              <option value="percentage">Percentage Scores</option>
              <option value="raw">Raw Scores</option>
            </select>
          </div>
        </ConfirmationModal>

        <ConfirmationModal
          isOpen={isModalOpen}
          message={message}
          onClose={handleCloseModal}
          onConfirm={handleConfirmPublish}
        />

        <EmailGradesModal
          isOpen={isEmailGradesModalOpen}
          isLoading={isEmailingGrades}
          onClose={handleSkipEmailGrades}
          onConfirm={sendGradeEmails}
        />
      </div>
    </div>
  );
}

export default TopBar;
